import { MindBlocks } from '~/curriculum/types/mapping-quiz.types'
import { GameActivityTotals } from '~/redux/apis/user-stats-api'

import {
  AccumulatedStats,
  GradeStats,
  HeaderStats,
  MustKnowTopicProgress,
  TopicStats,
  UserTimeResponse,
} from '~/statistics-v2/types/statistics.types'

/** What every figure reads before any payload has landed. */
export const EMPTY_HEADER_STATS: HeaderStats = {
  timeSpent: 0,
  totalXP: 0,
  videoWatchTime: 0,
  accuracy: 0,
  accuracyWithMustKnows: 0,
  activities: 0,
  showAccuracyWithMustKnows: false,
}

/**
 * Sources outside the curriculum payload that contribute to the header figures. `testPrep` is
 * structural rather than importing `TestPrepPanelFigures`, so this helper stays free of hooks.
 */
export interface HeaderStatsExtras {
  games?: GameActivityTotals
  testPrep?: { activities: number; points: number; possiblePoints: number; xp: number; timeSpentMs: number }
}

/**
 * The stats API's grade bucket key, e.g. `us-grade-5` — the shape `curriculum_level_progress` rows
 * carry and the key `getUserStats` returns its `grades` map under. Kept in one place so the header
 * figures and the game panel can never disagree about how it is spelled.
 */
export function toGradeKey(countryCode: string, gradeKey: string): string {
  return `${countryCode}-grade-${gradeKey}`
}

/**
 * The five headline figures, from one grade's stats (or `overall`) plus that grade's time.
 *
 * Pure — it fetches nothing, so the caller decides whose stats these are.
 *
 * ⚠ `showAccuracyWithMustKnows` picks which of the two accuracies the panel renders; both are always
 * returned. Pass `true` for all-grades, `false` for a single grade.
 *
 * ⚠ Time is in milliseconds, straight from `/get-user-time`.
 *
 * `extras` folds the sources that live outside the curriculum payload into the same five figures,
 * XP included — game XP comes from the reward ledger, which is the only place it exists.
 * Every source contributes all of its attempts to `activities`, but only its settled result to the
 * two accuracies: for games that is the latest attempt of each thing played, so correcting earlier
 * mistakes reaches 100%; for test prep it is points over possible points. Omit `extras` and the
 * figures are curriculum-only.
 *
 * Kept here rather than in the caller on purpose. Both sources were folded separately once, in two
 * places, and a merge nearly dropped one of them silently — anything added later belongs here too.
 */
export function toHeaderStats(
  stats: AccumulatedStats | null | undefined,
  time: UserTimeResponse | undefined,
  showAccuracyWithMustKnows: boolean,
  extras?: HeaderStatsExtras,
): HeaderStats {
  /*
   * ⚠ Absent curriculum stats must NOT discard the extras. `stats` is `grades[userGrade]`, which is
   * missing whenever the payload carries no entry for that key — a learner who played games on a
   * grade without touching its curriculum, or a grade key that does not match what the payload
   * holds. Returning EMPTY there would show 0 activities and 0% to someone with real play behind
   * them. Only report nothing when there is genuinely nothing.
   */
  if (!stats && !extras?.games && !extras?.testPrep) return EMPTY_HEADER_STATS

  const gameActivities = extras?.games?.activities ?? 0
  const gameAttempted = extras?.games?.latestAttempted ?? 0
  const gameCorrect = extras?.games?.latestCorrect ?? 0

  const testPrepActivities = extras?.testPrep?.activities ?? 0
  const testPrepPossible = extras?.testPrep?.possiblePoints ?? 0
  const testPrepPoints = extras?.testPrep?.points ?? 0

  const totalQuestions = (stats?.totalQuestions || 0) + gameAttempted + testPrepPossible
  const correct = (stats?.correctQuestions || 0) + gameCorrect + testPrepPoints
  const questionsAttempted = totalQuestions + (stats?.totalMustKnowQuestionsAttempted || 0)
  const correctQuestions = correct + (stats?.completedMustKnowQuestionsAttempted || 0)

  return {
    timeSpent: (time?.timeSpent ?? 0) + (extras?.testPrep?.timeSpentMs ?? 0) + (extras?.games?.timeSpentMs ?? 0),
    videoWatchTime: time?.videoWatchTime ?? 0,
    /*
     * Test XP joins the grade's Total XP here; the widened view must NOT pass `testPrep` — its XP
     * comes from the rewards system, which already contains every test's reward.
     */
    totalXP: (stats?.totalXP || 0) + (stats?.mustKnowXP || 0) + (extras?.testPrep?.xp ?? 0) + (extras?.games?.xp ?? 0),
    accuracy: totalQuestions > 0 ? (correct / totalQuestions) * 100 : 0,
    accuracyWithMustKnows: questionsAttempted > 0 ? (correctQuestions / questionsAttempted) * 100 : 0,
    activities:
      (stats?.totalQuestionsAttempted || 0) +
      (stats?.totalMustKnowQuestionsAttempted || 0) +
      gameActivities +
      testPrepActivities,
    showAccuracyWithMustKnows,
  }
}

/**
 * The All-math figures: portal-wide time, XP and video REPLACE the curriculum ones rather than
 * adding to them, because `timeSpentReport` and `rewards_aggregate` already count games.
 *
 * ⚠ That replacement is the only thing stopping game time being counted twice here — `baseStats`
 * already folded it in. Pure and exported so the invariant can be tested; it used to live inline in
 * `useGetUserStats`, where nothing could reach it.
 *
 * ⚠ `accuracy` uses a different formula from the base — `completedQuestionsAttempted /
 * totalQuestionsAttempted`, where the base uses `correctQuestions / totalQuestions` — so this branch
 * rebuilds both accuracies and has to re-fold the game numbers itself.
 */
export function toAllMathHeaderStats(
  baseStats: HeaderStats,
  stats: AccumulatedStats | null | undefined,
  portal: { timeSpent: number; totalXP: number; videoWatchTime: number },
  unmapped: { attempts: number; correct: number },
  games?: GameActivityTotals,
): HeaderStats {
  const portalStats: HeaderStats = { ...baseStats, ...portal }

  if (!stats) return portalStats

  const gameAttempted = games?.latestAttempted || 0
  const gameCorrect = games?.latestCorrect || 0
  const questionsAttempted = (stats.totalQuestions || 0) + (stats.totalMustKnowQuestionsAttempted || 0)
  const correctQuestions = (stats.correctQuestions || 0) + (stats.completedMustKnowQuestionsAttempted || 0)
  const attempted = (stats.totalQuestionsAttempted || 0) + unmapped.attempts + gameAttempted

  return {
    ...portalStats,
    accuracy:
      attempted > 0
        ? (((stats.completedQuestionsAttempted || 0) + unmapped.correct + gameCorrect) / attempted) * 100
        : baseStats.accuracy,
    accuracyWithMustKnows:
      questionsAttempted + unmapped.attempts + gameAttempted > 0
        ? ((correctQuestions + unmapped.correct + gameCorrect) /
            (questionsAttempted + unmapped.attempts + gameAttempted)) *
          100
        : baseStats.accuracyWithMustKnows,
    // `baseStats.activities` already includes every game attempt via `toHeaderStats`.
    activities: baseStats.activities + unmapped.attempts,
  }
}

export function combineTopicsStats(gradeDetails: GradeStats): TopicStats[] {
  return Object.values(gradeDetails.subdivisions ?? {}).flatMap((subdivision) =>
    Object.entries(subdivision.topics ?? {}).map(([topicId, topic]) => ({
      ...topic,
      topicId,
    })),
  )
}

/**
 * The stats API returns the whole topic's must-knows on every subtopic. The mapping-quiz prerequisite
 * tasks are keyed per subtopic, so we use a subtopic's blocks to scope the list down to its own must-knows,
 * keeping the accurate completion status from the stats progress (matched by title) where available.
 */
export function getSubtopicMustKnowsProgress(
  topicMustKnowsProgress: MustKnowTopicProgress[] | undefined,
  subtopicBlocks: MindBlocks[] | undefined,
): MustKnowTopicProgress[] {
  if (!subtopicBlocks?.length) return []

  const progressByTitle = new Map((topicMustKnowsProgress ?? []).map((progress) => [progress.title, progress]))
  const seenSlugs = new Set<string>()

  return subtopicBlocks
    .filter((block) => {
      if (!block.slug) return true
      if (seenSlugs.has(block.slug)) return false
      seenSlugs.add(block.slug)
      return true
    })
    .map(
      (block) =>
        progressByTitle.get(block.title) ?? {
          title: block.title,
          isCompleted: block.isCompleted,
          allLevels: block.levels?.length ?? 0,
          completedLevels: 0,
        },
    )
}
